import {
  ActionableDataTable,
  ActionableDataTableActionTypes,
} from '../../common/table/ActionableDataTable';
import { humanReadableCriteria } from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { DataTableOnOrderChangeClbk } from '../../common/table/TableTypes';
import { usePermissions } from '../../../permissions/usePermissions';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { useTerritoriesActions } from '../../../hooks/useTerritoriesActions';
import { selectTerritories } from '../../../reducers/territoriesReducer';
import { useRunOnce } from '../../common/useRunOnce';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import { DataTableId } from '../../common/table/utils';
import { SweepCriterion } from '../../../types/SweepCriterion';
import { CrmOrgPermission } from '../../../permissions/permissions.types';

interface PriorityRulesTableProps {
  rules: PriorityRule[];
  onRulesChange: (rules: PriorityRule[]) => void;
  onRuleEdit: (ruleId: string) => void;
  onRuleDuplicate: (ruleId: string) => void;
  readonly?: boolean;
  minRules?: number;
  tableId: DataTableId;
}

const columns = [
  {
    field: 'priority',
    headerName: 'Priority',
    width: '78px',
    minWidth: '98px',
  },
  { field: 'name', headerName: 'Name', minWidth: '200px' },
  { field: 'rule', headerName: 'Rule', minWidth: '300px' },
];

interface PriorityRulesDataRow {
  id: string;
  priority: string;
  name: string;
  rule: string;
}

export const PriorityRulesTable = ({
  rules,
  onRulesChange,
  onRuleEdit,
  onRuleDuplicate,
  readonly,
  minRules = 1,
  tableId,
}: PriorityRulesTableProps) => {
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId) ?? '';
  const permissionString: CrmOrgPermission[] = ['edit:dedupe-matching'];
  const { evaluatePermissions } = usePermissions();
  const [isAllowedBtn] = evaluatePermissions(
    [{ permission: permissionString, crmOrgId }]
  );

  const territories = useSelector(selectTerritories);
  const { getTerritories } = useTerritoriesActions();
  const { enrichCriteriaWithLabelsAndSfdcDisplayType } = useSweepFieldsInfo();

  useRunOnce(async () => {
    await getTerritories();
  });

  const rows: PriorityRulesDataRow[] = rules.map((rule, idx) => ({
    id: rule._id,
    priority: '' + (idx + 1),
    name: rule.name,
    rule: humanReadableCriteria(
      rule.when?.criteria?.length
        ? rule.when.criteria.map((c: SweepCriterion<string>) =>
          enrichCriteriaWithLabelsAndSfdcDisplayType(c),
        )
        : [],
      rule.when?.criteriaLogic,
      territories,
    ),
    // Disable delete action if at minimum rules
    disabledActions: rules.length <= minRules ? [ActionableDataTableActionTypes.DELETE] : undefined,
  }));

  const onOrderChange: DataTableOnOrderChangeClbk = ({ sourceIndex, destinationIndex }) => {
    const newRules = [...rules];
    newRules.splice(sourceIndex, 1);
    newRules.splice(destinationIndex, 0, rules[sourceIndex]);
    onRulesChange(newRules);
  };

  const getActions =
    !isAllowedBtn || readonly
      ? []
      : [
        { type: ActionableDataTableActionTypes.EDIT },
        {
          type: ActionableDataTableActionTypes.DUPLICATE,
        },
        {
          type: ActionableDataTableActionTypes.DELETE,
          confirm: true,
          confirmButtonOkLabel: 'Yes, Delete',
          confirmButtonCancelLabel: 'Cancel',
          confirmTitle: 'Delete Rule',
          confirmSubtitle: 'Are you sure you want to delete this rule?',
        },
      ];

  return (
    <ActionableDataTable
      tableId={tableId}
      allowRowReorder={isAllowedBtn && !readonly}
      columns={columns}
      rows={rows}
      {...(isAllowedBtn && !readonly ? { onRowOrderChange: onOrderChange } : {})}
      actions={getActions}
      onActionClick={({ actionType, row }) => {
        switch (actionType) {
          case ActionableDataTableActionTypes.DELETE:
            const newRules = rules.filter((r) => r._id !== row.id);
            onRulesChange(newRules);
            break;
          case ActionableDataTableActionTypes.EDIT:
            onRuleEdit(row.id);
            break;
          case ActionableDataTableActionTypes.DUPLICATE:
            onRuleDuplicate(row.id);
            break;
        }
      }}
      onRowClick={(
        e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
        row: PriorityRulesDataRow,
      ) => {
        onRuleEdit(row.id);
      }}
    />
  );
};
